import '../global.css'
import '../lib/suppressKnownWarnings'
import * as Sentry from '@sentry/react-native'
import React, { useEffect, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { DarkTheme, DefaultTheme, Stack, ThemeProvider as NavigationThemeProvider, useRouter, usePathname } from 'expo-router'
import * as Notifications from 'expo-notifications'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { QueryClientProvider } from '@tanstack/react-query'
import { StatusBar } from 'expo-status-bar'
import { useFonts } from 'expo-font'
import { AuthProvider, useAuth } from '../hooks/useAuth'
import { useTurnNotifier } from '../hooks/useTurnNotifier'
import type { GameResponse } from '@warcarp/api-types'
import { ThemeProvider } from '../hooks/useTheme'
import { ToastProvider } from '../components/ui/Toast'
import { queryClient } from '../lib/queryClient'
import { BUILD_LABEL } from '../lib/appVersion'
import { reportError } from '../lib/reporting'
import {
  isAppActive,
  refetchActiveQueries,
  trackAppActivity,
  type ForegroundTracker,
} from '../lib/appLifecycle'
import { NotificationBanner } from '../components/ui/NotificationBanner'
import { WebUpdateToast } from '../components/ui/WebUpdateToast'
import { ThemeScope } from '../components/ui/ThemeScope'
import {
  parseGameIdFromPushUrl,
  isViewingGame,
  gameIdFromPathname,
  isRecentlyExitedGame,
  gameRouteFromPushData,
  decideTurnBanner,
  isRecentTurnBanner,
  admitTurnBanner,
  clearTurnBannerForViewedGame,
  presentedTurnNotificationIdsForGame,
  subscribeToYourTurn,
  type TurnBannerHistory,
  type YourTurnSocketEvent,
} from '../lib/notificationBanner'

// ── Sentry — initialised before any provider mounts ──────────────────────────
// One Sentry project (`warcarp-app`) covers EVERY client build — iOS, Android,
// and the warcarp.com web bundle. The DSN is a public, write-only client key
// (already committed in eas.json), so we compile a fallback literal in here as
// the single source of truth: that guarantees Sentry is "built in" to every
// build regardless of whether a per-target env var was wired up. `expo export
// --platform web` doesn't read eas.json, and the EAS dev profiles don't set
// the DSN — the literal covers both gaps so we're never silently blind to a
// front-end error. `EXPO_PUBLIC_SENTRY_DSN` still overrides at bundle time
// (and setting it to "" is the escape hatch to force-disable).
//
// `enabled` is gated on `!__DEV__`: production-class builds (native release +
// web export) report; local Metro dev and native dev-client builds stay silent
// so day-to-day development doesn't flood the project. On web the
// @sentry/react-native SDK detects react-native-web via `isWeb()` and uses the
// bundled @sentry/browser transport automatically — no separate @sentry/react.
const SENTRY_DSN =
  process.env.EXPO_PUBLIC_SENTRY_DSN ??
  'https://2392287a222026e3f1916927d266a7b8@o4511508359151616.ingest.us.sentry.io/4511508378615808'
Sentry.init({
  dsn: SENTRY_DSN,
  enabled: !!SENTRY_DSN && !__DEV__,
  // A labelled local test build reports as 'test' so it never mixes with
  // production issues (lib/appVersion.ts).
  environment: __DEV__ ? 'development' : BUILD_LABEL ? 'test' : 'production',
  // 1.0 in both dev and prod for now — current traffic is low enough
  // that full sampling gives complete visibility into client perf and
  // costs nothing. Dial down via a `tracesSampleRate` override here
  // (no EXPO_PUBLIC_* env var because Expo Web inlines these at build
  // time, so any runtime change requires a rebuild anyway).
  tracesSampleRate: 1.0,
  enableAutoSessionTracking: true,
  // Browser-extension noise. On web our bundle shares a JS context with
  // whatever extensions the visitor has installed, and their failures surface
  // as OUR errors with our release tag — e.g. "Invalid call to
  // runtime.sendMessage(). Tab not found." (a chrome.runtime API we never
  // call). Nothing here is reachable from app code, so matching by message is
  // safe and keeps the project's signal-to-noise usable.
  ignoreErrors: [
    /runtime\.sendMessage/i,
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /^ResizeObserver loop/i,
  ],
  // Drop anything originating from a LOCAL dev server. `enabled` already
  // gates on !__DEV__, but a preview/TestFlight build pointed at a laptop
  // still reports — that is where "TypeError: Failed to fetch
  // (localhost:4123)" came from. Those events are never actionable in
  // production triage.
  beforeSend(event) {
    const req = event.request?.url ?? ''
    if (/^https?:\/\/(localhost|127\.0\.0\.1|10\.0\.2\.2|\[::1\])[:/]/i.test(req)) return null
    return event
  },
})

/**
 * Safari pinch-zoom / elastic-pan fix (web only).
 *
 * Injected once at the root level so it applies across the whole app
 * without relying on per-component inline styles. Three rules:
 *
 *   html, body { touch-action: pan-y; }
 *     Allows vertical scrolling on the page body while blocking the
 *     browser's native pinch-zoom and horizontal swipe-back, which both
 *     conflict with our map PanResponder drag gesture. `pan-y` (not `none`)
 *     preserves pull-to-refresh on iOS if ever needed.
 *
 *   html, body { overscroll-behavior: contain; }
 *     Stops elastic-bounce scroll from propagating to a parent scroller
 *     (or the page itself) when the user reaches the end of a scrollable
 *     list, preventing the whole page from lurching. Does not break the
 *     map's own drag gesture since the map container is not a scroll view.
 *
 * We use `pan-y` rather than `none` to preserve vertical scroll on
 * content that genuinely scrolls (e.g. the lobby setup sheet).
 * If this causes swipe-back to stop working on a future page that needs
 * it, the fix is to set `touch-action: auto` on that specific container.
 */
const WEB_TOUCH_STYLE =
  'html,body{touch-action:pan-y;overscroll-behavior:contain;-webkit-user-select:none;user-select:none;}'

interface PendingBanner {
  title: string
  body: string
  gameId: string
  occurredAt: number
}

/**
 * Deep-links the app into a game when the user taps a push notification.
 *
 * Uses `useLastNotificationResponse()` rather than only
 * `addNotificationResponseReceivedListener` because the listener does NOT fire
 * for the notification that COLD-STARTS the app from a killed state — that was
 * the "tapping my-turn notification doesn't open the game" bug on Android. The
 * hook returns the launching tap too, so both cold-start and
 * background-resume taps deep-link.
 *
 * Lives inside AuthProvider so it can wait for auth to resolve before
 * navigating; if the user isn't signed in, the (app) route guard handles the
 * login bounce + redirect-back automatically once we navigate to the game URL.
 */
function NotificationDeepLinkHandler() {
  const router = useRouter()
  const { isLoading } = useAuth()
  const lastResponse = Notifications.useLastNotificationResponse()
  const handledIdRef = React.useRef<string | null>(null)

  useEffect(() => {
    if (!lastResponse) return
    // Wait until auth has resolved so we don't race the (app) route guard.
    if (isLoading) return
    const request = lastResponse.notification.request
    if (handledIdRef.current === request.identifier) return

    const route = gameRouteFromPushData(
      request.content.data as Record<string, unknown> | undefined,
    )
    if (!route) return

    handledIdRef.current = request.identifier
    router.push(route as never)
  }, [lastResponse, isLoading, router])

  return null
}

/**
 * Watches the viewer's games list (polled) and raises the in-app "your turn"
 * banner when a DIFFERENT game flips to their turn — the push-INDEPENDENT path
 * so it works in the lobby / inside another game even when push (Android FCM)
 * doesn't deliver. Lives inside AuthProvider (needs the signed-in user).
 */
function TurnWatcher({ onNewTurn }: { onNewTurn: (game: GameResponse) => void }) {
  useTurnNotifier(onNewTurn)
  return null
}

/**
 * Refreshes visible app data when the process/tab returns to the user after a
 * meaningful background stay.
 *
 * React Navigation focus is not the same thing as browser visibility or native
 * AppState — and the long-lived queries that matter most here (chat, event
 * log) deliberately disable refetchOnWindowFocus because the socket covers
 * them while connected. The server pushes no snapshot on WS (re)connect, so
 * anything missed while the socket was dead in the background only arrives
 * via this explicit refetch. Cold starts need no handling: a fresh
 * process/tab has an empty cache, so every query fetches on mount anyway.
 */
function AppLifecycleHandler() {
  const platform = Platform.OS === 'web' ? 'web' : 'native'
  const trackerRef = React.useRef<ForegroundTracker>({ active: true, backgroundedAt: null })

  useEffect(() => {
    const report = (isActive: boolean) => {
      const { next, refresh } = trackAppActivity(trackerRef.current, isActive, Date.now())
      trackerRef.current = next
      if (refresh !== 'none') refetchActiveQueries(queryClient, refresh)
    }

    if (platform === 'native') {
      trackerRef.current = {
        active: isAppActive('native', undefined, AppState.currentState),
        backgroundedAt: null,
      }
      const subscription = AppState.addEventListener('change', (nextState) => {
        report(isAppActive('native', undefined, nextState))
      })
      return () => subscription.remove()
    }

    if (typeof document === 'undefined') return
    trackerRef.current = {
      active: isAppActive('web', document.visibilityState, undefined),
      backgroundedAt: null,
    }
    const onVisibilityChange = () => {
      report(isAppActive('web', document.visibilityState, undefined))
    }
    // Window blur/focus cover desktop window switches, where the tab stays
    // visible so no visibilitychange ever fires — without these, returning to
    // an always-visible browser window would never refresh. The tracker's
    // same-state check prevents duplicate refreshes when the browser emits
    // several of these events for one transition (e.g. focus AND
    // visibilitychange on a tab switch).
    const onFocus = () => report(true)
    const onBlur = () => report(false)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
    }
  }, [platform])

  return null
}

function RootLayout() {
  // Expo Font loads these bundled faces on both web and native. Wait for
  // them before the first app frame so fallback metrics cannot move the UI.
  // Font errors still render with fallbacks rather than leaving a blank app.
  const [fontsLoaded, fontError] = useFonts({
    'Inter': require('../assets/fonts/Inter-Regular.ttf'),
    'Bungee': require('../assets/fonts/Bungee-Regular.ttf'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    'Zilla Slab': require('../assets/fonts/ZillaSlab-Regular.ttf'),
  })

  useEffect(() => {
    if (fontError) reportError(fontError, { tags: { source: 'font-loading' } })
  }, [fontError])

  const router = useRouter()
  const pathname = usePathname()
  const [pendingBanner, setPendingBanner] = useState<PendingBanner | null>(null)
  const previousGamePathRef = React.useRef<string | null>(null)
  const recentlyExitedGameRef = React.useRef<{ gameId: string; exitedAt: number } | null>(null)
  const viewingGameIdRef = React.useRef<string | null>(gameIdFromPathname(pathname))

  // Keep a small navigation context so a game's own turn state does not
  // announce itself as a new turn when the player returns to the lobby.
  useEffect(() => {
    const currentGameId = gameIdFromPathname(pathname)
    const previousGameId = previousGamePathRef.current
    if (previousGameId && previousGameId !== currentGameId) {
      recentlyExitedGameRef.current = { gameId: previousGameId, exitedAt: Date.now() }
    }
    previousGamePathRef.current = currentGameId
    viewingGameIdRef.current = currentGameId
    // Entering a game (via the banner, a games-list row or a deep link)
    // retires the banner for that game; a banner for another game stays.
    setPendingBanner((current) => clearTurnBannerForViewedGame(current, currentGameId))
    // Native: the foreground handler lists turn pushes in the system tray
    // without a banner. Clear that game's turn notifications once it is open.
    if (currentGameId && Platform.OS !== 'web') {
      void Notifications.getPresentedNotificationsAsync()
        .then((presented) => Promise.all(
          presentedTurnNotificationIdsForGame(presented, currentGameId)
            .map((id) => Notifications.dismissNotificationAsync(id)),
        ))
        .catch((err: unknown) => reportError(err, { tags: { source: 'turn-notification-dismiss' } }))
    }
  }, [pathname])

  const shouldSuppressRecentlyExitedGame = React.useCallback((gameId: string) => {
    return isRecentlyExitedGame(recentlyExitedGameRef.current, gameId)
  }, [])

  // Persistent per-game banner history — dedupes across BOTH banner paths
  // (push arrival + poll watcher) and OUTLIVES the visible banner. The old
  // dedup compared only against the banner currently on screen, so when the
  // push-path banner auto-dismissed, the next poll tick raised a duplicate
  // for the same turn. See decideTurnBanner in lib/notificationBanner.
  const bannerHistoryRef = React.useRef(new Map<string, TurnBannerHistory>())
  const appStartedAtRef = React.useRef(Date.now())

  /** Returns true (and records it) when a banner for this game/turn should show. */
  const shouldBanner = React.useCallback((gameId: string, turn: number | null) => {
    const history = bannerHistoryRef.current.get(gameId) ?? {
      renderedTurns: new Set<number>(),
      lastBannerAt: null,
      lastBannerWasUnknown: false,
    }
    const { show, history: nextHistory } = decideTurnBanner({
      history,
      turn,
      now: Date.now(),
      appStartedAt: appStartedAtRef.current,
    })
    if (show) bannerHistoryRef.current.set(gameId, nextHistory)
    return show
  }, [])

  // There is deliberately one foreground slot, not a queue. If several games
  // become actionable while the app is resuming, retain only the newest fresh
  // event and discard older arrivals. Every path (poll, socket, push) funnels
  // through here, so an event for the game on screen is always dropped.
  const enqueueBanner = React.useCallback((next: PendingBanner) => {
    if (!isRecentTurnBanner(next.occurredAt)) return
    setPendingBanner((current) => admitTurnBanner(current, next, viewingGameIdRef.current))
  }, [])

  // Raise the banner for a newly-our-turn game (poll-watcher path).
  const handleNewTurn = React.useCallback(
    (game: GameResponse) => {
      const occurredAt = Date.parse(game.turnStartedAt ?? '')
      if (!isRecentTurnBanner(occurredAt)) return
      if (game.id === viewingGameIdRef.current) return
      if (shouldSuppressRecentlyExitedGame(game.id)) return
      if (!shouldBanner(game.id, game.currentTurn ?? null)) return
      enqueueBanner({
        title: 'Your Turn!',
        body: `It's your turn in ${game.name}`,
        gameId: game.id,
        occurredAt,
      })
    },
    [enqueueBanner, shouldBanner, shouldSuppressRecentlyExitedGame],
  )

  // A game screen's socket receives private YOUR_TURN events for this user,
  // including events for other games. Bridge those events to the root banner;
  // the current game is still excluded because its turn UI is already visible.
  const handleSocketTurn = React.useCallback(
    (event: YourTurnSocketEvent) => {
      if (isViewingGame(pathname, event.gameId)) return
      if (shouldSuppressRecentlyExitedGame(event.gameId)) return
      if (!isRecentTurnBanner(event.occurredAt)) return
      if (!shouldBanner(event.gameId, event.turn)) return
      enqueueBanner({
        title: 'Your Turn!',
        body: `It's your turn in ${event.gameName}`,
        gameId: event.gameId,
        occurredAt: event.occurredAt,
      })
    },
    [enqueueBanner, pathname, shouldBanner, shouldSuppressRecentlyExitedGame],
  )

  useEffect(() => subscribeToYourTurn(handleSocketTurn), [handleSocketTurn])

  // Arrival listener (foreground only): fires when a push arrives while the
  // app is open. We show our own in-app banner instead of the native one
  // (which is suppressed in notifications.ts setNotificationHandler).
  // Skip entirely on web — push notifications work differently there and
  // the browser handles its own notification UI.
  useEffect(() => {
    if (Platform.OS === 'web') return

    const sub = Notifications.addNotificationReceivedListener((notification) => {
      const content = notification.request.content
      if (content.data?.kind === 'SUPPORT_TICKET') {
        void queryClient.invalidateQueries({ queryKey: ['support'] })
        return
      }
      // Only a real turn push may raise the foreground turn banner. Stale-turn
      // reminders also target a game URL, but are not a new turn event.
      if (content.data?.kind !== 'YOUR_TURN') return
      const url = content.data?.url as string | undefined
      const gameId = parseGameIdFromPushUrl(url)

      // Only show a banner for game-targeted pushes.
      if (!gameId) return

      // Suppress when the user is already viewing that game's screen.
      if (isViewingGame(pathname, gameId)) return

      const turn = typeof content.data?.turn === 'number' ? content.data.turn : null
      const occurredAt =
        typeof content.data?.occurredAt === 'number'
          ? content.data.occurredAt
          : typeof content.data?.occurredAt === 'string'
            ? Date.parse(content.data.occurredAt)
            : null
      if (typeof occurredAt !== 'number') return
      if (!isRecentTurnBanner(occurredAt)) return
      if (!shouldBanner(gameId, turn)) return

      enqueueBanner({
        title: content.title ?? 'WarCarp',
        body: content.body ?? "It's your turn.",
        gameId,
        occurredAt,
      })
    })
    return () => sub.remove()
    // pathname intentionally not in deps — we capture the ref at the moment
    // the notification arrives (closures are fine; the listener is
    // re-registered on each pathname change so `pathname` is always fresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueBanner, pathname, shouldBanner])

  // All hooks above — safe to early-return from here onward.
  //
  // First paint uses the final font metrics on web as well as native.
  if (!fontsLoaded && !fontError) {
    return null
  }

  return (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <AppLifecycleHandler />
              {/* Inject Safari touch-action fix once at the root, web only.
                  dangerouslySetInnerHTML is intentional here — the content
                  is a static string literal, not user data, so there is
                  no XSS risk. The cast to `any` is needed because React
                  Native's View type doesn't include HTML attributes; on
                  web this renders as a real <style> element. */}
              {Platform.OS === 'web' && (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                React.createElement('style', {
                  dangerouslySetInnerHTML: { __html: WEB_TOUCH_STYLE },
                } as any)
              )}
              {/* Native only: useLastNotificationResponse() calls
                  ExpoNotifications.getLastNotificationResponse, which does NOT
                  exist on web and throws during render → white-screens the
                  whole web app. Push deep-linking is a native-only concern. */}
              {Platform.OS !== 'web' && <NotificationDeepLinkHandler />}
              {/* Push-independent cross-game "your turn" watcher. It polls on
                  every platform, so desktop web gets the same in-app banner
                  even though browser push is not configured. */}
              <TurnWatcher onNewTurn={handleNewTurn} />
              <ThemedShell />
              {/* Web only: "Update ready" toast when a newer build ships
                  while this tab is open (lib/webUpdate). */}
              <WebUpdateToast />
              {/* In-app foreground notification banner. Rendered outside the
                  Stack so it floats above the entire navigation tree on both
                  native and web. */}
              {pendingBanner && (
                <NotificationBanner
                  title={pendingBanner.title}
                  body={pendingBanner.body}
                  onPress={() => {
                    setPendingBanner(null)
                    router.push(`/(app)/games/${pendingBanner.gameId}` as never)
                  }}
                  onDismiss={() => setPendingBanner(null)}
                />
              )}
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SafeAreaProvider>
  )
}

/**
 * Stack + status-bar wrapper that reads the active theme so the StatusBar
 * tint and Stack background swap with the palette.
 */
function ThemedShell() {
  const { theme } = require('../hooks/useTheme').useTheme()
  // Background swaps with the canvas token: light cream / night-ops bunker.
  const bg = theme === 'dark' ? '#1a1f12' : '#ece1c2'
  // Every navigator below (the app, auth and game stacks) paints its screen
  // containers from the navigation theme. Its default is light grey, which
  // showed as a light flicker on dark-mode screen loads before the screen's own
  // content drew. Give it the theme canvas so nothing light shows through.
  const navigationTheme = React.useMemo(() => {
    const base = theme === 'dark' ? DarkTheme : DefaultTheme
    return { ...base, colors: { ...base.colors, background: bg, card: bg } }
  }, [theme, bg])
  // Two-pronged dark-mode propagation for native iOS (the third
  // attempt). PR #323 wrapped vars in `@layer base` — not enough.
  // PR #336 applied `vars()` to this wrapper — also not enough on
  // iOS (vars don't propagate across react-native-screens stack
  // boundaries the way CSS variables do on web).
  //
  // Adding `className="dark"` is the missing piece. NativeWind 4
  // with `darkMode: 'class'` looks for a `.dark` ancestor in the
  // React tree (parallel to `documentElement.classList.add('dark')`
  // on web) — without that, the `.dark` CSS selector never matches
  // on native and the `:root` (light) variable values stick.
  // Keeping the `vars()` style as belt-and-suspenders: if the
  // class-tracking path also fails for any reason, the inline vars
  // are still in scope. Both live in the shared ThemeScope.
  return (
    <ThemeScope
      // backgroundColor (not just the Stack contentStyle) so the safe-area /
      // status-bar region behind the content paints the theme canvas instead
      // of the default window grey — that grey was showing as a band/line
      // above the content (esp. the map) in landscape.
      style={{ flex: 1, backgroundColor: bg }}
      className="flex-1"
    >
      <StatusBar style={theme === 'dark' ? 'light' : 'dark'} />
      {/* gestureEnabled:false — the iOS swipe-back gesture is disabled app-wide;
          it competed with panning the map and we navigate via explicit Back
          buttons everywhere. Set on every nested Stack too. */}
      <NavigationThemeProvider value={navigationTheme}>
        <Stack screenOptions={{ headerShown: false, gestureEnabled: false, contentStyle: { backgroundColor: bg } }} />
      </NavigationThemeProvider>
    </ThemeScope>
  )
}

// Wrap with Sentry to enable automatic error boundary + session tracking.
// Sentry.wrap is a thin HOC that adds an ErrorBoundary + initialises the
// native SDK lifecycle (appLoaded signal). Compatible with Expo Router —
// the default export of _layout.tsx is what Expo Router mounts as the root.
export default Sentry.wrap(RootLayout)
